import { useCallback, useEffect, useState, useRef } from "react";
import useFetch from "../../../../hooks/useFetch";
import { API } from "../../../../common/Constants";
import { getAPIHeaders } from "../../../../common/Utils";
import {
  Assessment,
  Subject,
  Syllabus,
} from "../../../../types/dataModel";
import PreLoader from "../../../../core/common/preLoader";
import CommonSelectV2 from "../../../../core/common/commonSelectV2";
import CustomModal from "../../../../core/common/customModal";

interface Props {
  assessment?: Assessment;
  onSuccess: () => void;
  onCancel: () => void;
  show: boolean
}

interface GenerationRequest {
  assessmentId?: number | undefined;
  subjectId?: number | undefined;
  syllabusId?: number;
  questionCount?: number;
  difficulty?: string;
  unitIds?: number[];
  sectionIds?: number[];
  customPrompt?: string;
  additionalParameters?: {
    model?: string;
    temperature?: number;
    maxTokens?: number;
    questionTypes?: string[];
  };
}

interface GeneratedQuestion {
  questionId: number;
  questionText: string;
  options: GeneratedOption[];
  explanation?: string;
  validationStatus?: string;
  difficulty?: string;
  localValidationStatus?: string;
}

interface GeneratedOption {
  optionId: number;
  optionText: string;
  isCorrect: boolean;
}

const DIFFICULTY_LEVELS = [               
  { value: "EASY", label: "Easy" },
  { value: "MEDIUM", label: "Medium" },
  { value: "HARD", label: "Hard" },
];

const AiAssessmentGeneration = ({ assessment, onSuccess, onCancel, show }: Props) => {
  const [generatedQuestions, setGeneratedQuestions] = useState<
    GeneratedQuestion[]
  >([]);
  const [showResults, setShowResults] = useState(false);
  const [subjectOpts, setSubjectOpts] = useState<{ value: any; label: string }[]>([])
  const [syllabusOpts, setSyllabusOpts] = useState<{ value: any; label: string }[]>([])
  const [formData, setFormData] = useState<GenerationRequest>({
                                                assessmentId: undefined,
                                                subjectId: undefined,
                                                questionCount: 5,
                                                difficulty: "MEDIUM",
                                              });
  const [showConfirmExitDialog, setShowConfirmExitDialog] = useState(false);

  const [msgBox, setMsgBox] = useState<{
    msg: string;
    error: boolean;
  }>({
    msg: "",
    error: false,
  });

  const { isLoading, callFetch } = useFetch();

  const [isBatchProcessing, setIsBatchProcessing] = useState<boolean>(false);
  const [batchStatus, setBatchStatus] = useState<any>(null);
  const batchStatusPollingIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (assessment?.id) {
      setFormData({
        ...formData,
        assessmentId: assessment.id,
      });
      fetchSubjectsForAssessment(assessment.id);
    }
  }, [assessment]);

  useEffect(() => {
    if (formData?.subjectId) {
      fetchSyllabusesForSubject(formData?.subjectId);
    } else {
      setSyllabusOpts([]);
    }
  }, [formData?.subjectId]);

  useEffect(() => {
    return () => {
      if (batchStatusPollingIdRef.current) {
        clearInterval(batchStatusPollingIdRef.current);
        batchStatusPollingIdRef.current = null;
      }
    };
  }, []);

  const fetchSubjectsForAssessment = (assessmentId: number) => {
    // First try to get subjects assigned to this assessment
    callFetch(
      `${API.GET_SUBJECTS_FOR_ASSESSNMENT}`.replace(
        ":assessmentId",
        assessmentId.toString()
      ),
      "GET",
      getAPIHeaders(),
      null,
      null
    )
      .then((res) => {
        if (res && Array.isArray(res)) {
          const subjectList: Subject[] = [];
          const subjectIds = new Set<number>();
          res.forEach((subject: Subject) => {
            if (subject.id) {
              subjectIds.add(subject.id);
              subjectList.push(subject);
            }
          });

          if (subjectList.length > 0) {
            setSubjectOpts(
              subjectList.map((subject: Subject) => {
                return {
                  value: subject.id!,
                  label: subject.title!,
                };
              })
            );
            if (subjectList.length === 1) {
              // Auto-select if there's only one subject
              setFormData({
                ...formData,
                assessmentId: assessmentId,
                subjectId: subjectList[0].id,
              });
            }
          } else {
            // If no subjects found, fetch all available subjects
            fetchAllSubjects();
          }
        } else {
          fetchAllSubjects();
        }
      })
      .catch(() => {
        fetchAllSubjects();
      });
  };

  const fetchAllSubjects = () => {
    callFetch(`${API.SUBJECT}`, "GET", getAPIHeaders(), null, null).then(
      (res) => {
        if (res && Array.isArray(res)) {
          setSubjectOpts(res?.filter(subject => subject.id != null && subject.title != null)
            .map((subject: Subject) => {
              return {
                value: subject.id!,
                label: subject.title!,
              }
            }));
          if (res.length === 1) {
            setFormData({
              ...formData,
              subjectId: res[0].id,
            });
          }
        }
      }
    );
  };

  const fetchSyllabusesForSubject = (subjectId: number) => {
    callFetch(
      `${API.GET_ALL_SYLLABUS_BY_SUBJECT}`.replace(
        ":subjectId",
        subjectId.toString()
      ),
      "GET",
      getAPIHeaders(),
      null,
      null
    ).then((res) => {
      if (res && Array.isArray(res)) {
        setSyllabusOpts(
          res?.filter(syllabus => syllabus.id != null && syllabus.title != null).map((syllabus: Syllabus) => {
            return {
              value: syllabus.id!,
              label: syllabus.title!,
            };
          }) || []
        );
        if (res.length === 1) {
          // Auto-select if there's only one syllabus
          setFormData({
            ...formData,
            syllabusId: res[0].id,
          });
        }
      }
    });
  };

  const handleSubjectChange = (value:any) => {
    const subjectId = value ? value : undefined;
    setFormData({
      ...formData,
      subjectId: subjectId,
      syllabusId: undefined,
    });
  };

  const handleSyllabusChange = (value:any) => {
    const syllabusId = value ? value : undefined;
    setFormData({
      ...formData,
      syllabusId: syllabusId,
    });
  };

  const validateForm = (): boolean => {
    if (!formData?.assessmentId) {
      setMsgBox({ msg: "Assessment ID is required", error: true });
      return false;
    }
    if (!formData?.subjectId) {
      setMsgBox({ msg: "Please select a subject", error: true });
      return false;
    }
    if (formData?.questionCount && formData?.questionCount < 1) {
      setMsgBox({ msg: "Number of questions must be at least 1", error: true });
      return false;
    }
    return true;
  };

  const handleGenerateQuestions = () => {
    if (!validateForm()) return;

    const requestData = { ...formData };

    // Optional fields that shouldn't be sent if empty
    if (!requestData.syllabusId) delete requestData.syllabusId;
    if (!requestData.customPrompt) delete requestData.customPrompt;
    if (!requestData.unitIds || requestData.unitIds.length === 0)
      delete requestData.unitIds;
    if (!requestData.sectionIds || requestData.sectionIds.length === 0)
      delete requestData.sectionIds;

    // Only include additionalParameters if they exist
    if (
      !requestData.additionalParameters ||
      Object.keys(requestData.additionalParameters).length === 0
    ) {
      delete requestData.additionalParameters;
    }

    // Reset any previous generation state
    setMsgBox({ msg: "", error: false });
    setIsBatchProcessing(true);
    setBatchStatus(null);
    setShowResults(false);
    setGeneratedQuestions([]); // Clear any previous questions

    callFetch(
      API.GENERATE_AI_ASSESSMENT,
      "POST",
      getAPIHeaders(),
      null,
      requestData
    )
      .then((res) => {
        if (
          res &&
          res.assessmentId &&
          res.generationParameters &&
          res.generationParameters.isBatchedGeneration === true
        ) {
          // Start polling for batch status
          setIsBatchProcessing(true);
          pollBatchStatus(res.assessmentId);
          setMsgBox({
            msg: "Question generation started. Please wait...",
            error: false,
          });
        } else if (res && res.questions && Array.isArray(res.questions)) {
          // Direct response with questions (non-batched processing)
          setGeneratedQuestions(res.questions);
          setShowResults(true);
          setIsBatchProcessing(false);
          setMsgBox({ msg: "Questions generated successfully", error: false });
        } else {
          setIsBatchProcessing(false);
          setMsgBox({ msg: "Failed to generate questions", error: true });
        }
      })
      .catch((err) => {
        setIsBatchProcessing(false);
        setMsgBox({
          msg: `Failed to generate questions: ${
            err?.response?.data || "Unknown error"
          }`,
          error: true,
        });
      });
  };

  // Function to poll batch status
  const pollBatchStatus = useCallback((assessmentId: number) => {
    if (!assessmentId) return;

    // Clear any existing polling interval
    if (batchStatusPollingIdRef.current) {
      clearInterval(batchStatusPollingIdRef.current);
      batchStatusPollingIdRef.current = null;
    }

    const fetchStatus = () => {
      callFetch(
        `${API.AI_ASSESSMENT}/assessment/${assessmentId}/batch-status`,
        "GET",
        getAPIHeaders(),
        null,
        null
      )
        .then((res) => {
          if (res) {
            setBatchStatus(res);

            // If batch processing is complete, stop polling and show results
            if (res.isComplete) {
              setIsBatchProcessing(false);

              // Fetch the final questions
              fetchGeneratedQuestions(assessmentId);

              // Clear the polling interval
              if (batchStatusPollingIdRef.current) {
                clearInterval(batchStatusPollingIdRef.current);
                batchStatusPollingIdRef.current = null;
              }

              setMsgBox({
                msg: "Questions generated successfully",
                error: false,
              });
            }
          }
        })
        .catch((err) => {
          // On error, stop polling and show error message
          setIsBatchProcessing(false);
          if (batchStatusPollingIdRef.current) {
            clearInterval(batchStatusPollingIdRef.current);
            batchStatusPollingIdRef.current = null;
          }
          setMsgBox({
            msg: `Failed to check generation status: ${
              err.message || "Unknown error"
            }`,
            error: true,
          });
        });
    };

    // Initial fetch
    fetchStatus();

    // Start polling every 2 seconds
    const intervalId = window.setInterval(fetchStatus, 2000);
    batchStatusPollingIdRef.current = intervalId;
  }, []);

  // Function to fetch generated questions for an assessment
  const fetchGeneratedQuestions = (assessmentId: number) => {
    callFetch(
      `${API.AI_ASSESSMENT}/assessment/${assessmentId}/generations`,
      "GET",
      getAPIHeaders(),
      null,
      null
    )
      .then((res) => {
        if (res && Array.isArray(res) && res.length > 0) {
          // Use a Map to store unique questions by questionId
          const questionsMap = new Map<number, GeneratedQuestion>();

          // Process each generation batch
          res.forEach((generation: any) => {
            if (generation.questions && Array.isArray(generation.questions)) {
              generation.questions.forEach((question: GeneratedQuestion) => {
                questionsMap.set(question.questionId, question);
              });
            }
            //}
          });

          const allQuestions = Array.from(questionsMap.values());

          setGeneratedQuestions(allQuestions);
          setShowResults(true);
        } else {
          setMsgBox({ msg: "No questions were generated", error: true });
        }
      })
      .catch((err) => {
        setMsgBox({
          msg: `Failed to fetch generated questions: ${
            err.message || "Unknown error"
          }`,
          error: true,
        });
      });
  };

  const handleUpdateQuestion = (
    questionId: number,
    status: string,
    feedback?: string
  ) => {
    setGeneratedQuestions(
      generatedQuestions.map((q) =>
        q.questionId === questionId
          ? { ...q, localValidationStatus: status }
          : q
      )
    );
    setMsgBox({
      msg: `Question ${
        status === "APPROVED" ? "approved" : "rejected"
      }. Changes will be saved when you click Done.`,
      error: false,
    });
  };

  const handleAcceptQuestion = (questionId: number) => {
    handleUpdateQuestion(questionId, "APPROVED");
  };

  const handleRejectQuestion = (questionId: number) => {
    handleUpdateQuestion(questionId, "REJECTED");
  };

  // Check if all questions have been reviewed (either approved or rejected)
  const areAllQuestionsReviewed = (): boolean => {
    if (generatedQuestions.length === 0) return false;

    return generatedQuestions.every((question) => {
      // Check both localValidationStatus and validationStatus
      return (
        question.localValidationStatus === "APPROVED" ||
        question.localValidationStatus === "REJECTED" ||
        (!question.localValidationStatus &&
          (question.validationStatus === "APPROVED" ||
            question.validationStatus === "REJECTED"))
      );
    });
  };

  const reset = () => {
    setFormData({
      assessmentId: assessment?.id,
      subjectId: undefined,
      questionCount: 5,
      difficulty: "MEDIUM",
    });
    setShowResults(false);
    setGeneratedQuestions([]);
    setMsgBox({ msg: "", error: false });
    setIsBatchProcessing(false);
    setBatchStatus(null);

    // Clear any polling interval
    if (batchStatusPollingIdRef.current) {
      clearInterval(batchStatusPollingIdRef.current);
      batchStatusPollingIdRef.current = null;
    }
  };

  const exit = () => {
    if (generatedQuestions.length && !areAllQuestionsReviewed()) {
      setShowConfirmExitDialog(true);
    } else {
      reset();
      onCancel();
    }
  };

  const renderForm = () => {
    return (
      <>
        <div className="alert alert-info" role="alert">
          <h6 className="mb-2">
            <i className="ti ti-bulb me-2"></i>Generate AI-powered assessment
            questions
          </h6>
          <p className="mb-0">
            Fill in the details below to generate questions using artificial
            intelligence. The AI will analyze subject content and create
            relevant questions based on your parameters.
          </p>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="form-group mb-3">
              <label className="form-label">
                Subject <span className="text-danger">*</span>
              </label>
              <CommonSelectV2
                value={subjectOpts.find((subject) => subject.value === formData?.subjectId)}
                onChange={handleSubjectChange}
                options={subjectOpts}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group mb-3">
              <label className="form-label">Syllabus</label>
              <CommonSelectV2
                value={syllabusOpts.find((syllabus) => syllabus.value === formData?.syllabusId)}
                onChange={handleSyllabusChange}
                options={syllabusOpts}
                disabled={!formData?.subjectId}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6">
            <div className="form-group mb-3">
              <label className="form-label">
                Number of Questions <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                name="questionCount"
                min="1"
                max="20"
                value={formData?.questionCount}
                onChange={(e)=>{
                    const { value } = e.target;
                    if(value){
                    setFormData({
                        ...formData,
                        questionCount: value ? parseInt(value) : 0
                      });
                    }
                }}
                className="form-control"
                required
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group mb-3">
              <label className="form-label">
                Difficulty Level <span className="text-danger">*</span>
              </label>
              <CommonSelectV2
                value={DIFFICULTY_LEVELS.find((level) => level.value === formData?.difficulty)}
                onChange={(value) =>
                  setFormData({ ...formData, difficulty: value })
                }
                options={DIFFICULTY_LEVELS}
                />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <div className="form-group mb-3">
              <label className="form-label">Custom Prompt (Optional)</label>
              <textarea
                name="customPrompt"
                value={formData?.customPrompt || ""}
                onChange={(e)=>{
                  const { value } = e.target;
                  setFormData({
                    ...formData,
                    customPrompt: value,
                  });
                }}
                className="form-control"
                rows={3}
                placeholder="E.g., Focus on application-based questions that test critical thinking. Include at least 3 questions on problem-solving techniques."
              ></textarea>
              <small className="text-muted">
                Add specific instructions to guide the AI in generating
                questions.
              </small>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderResults = () => {
    if (generatedQuestions.length === 0) {
      // If questions are still being generated, show a different message
      if (isBatchProcessing) {
        return (
          <div className="alert alert-primary" role="alert">
            <h6 className="mb-2">
              <i className="ti ti-loader me-2 spinner-border spinner-border-sm"></i>
              Questions Being Generated
            </h6>
            <p className="mb-0">
              Questions are currently being generated. Please wait for the
              process to complete.
            </p>
          </div>
        );
      }

      // If no questions and not processing, show the "no questions" message
      return (
        <div className="alert alert-info" role="alert">
          <h6 className="mb-2">
            <i className="ti ti-info-circle me-2"></i>No Questions Available
          </h6>
          <p className="mb-0">
            No questions have been generated yet. Please try generating
            questions or wait for the current generation to complete.
          </p>
        </div>
      );
    }

    // Calculate review status counts - using localValidationStatus first, falling back to validationStatus
    const totalQuestions = generatedQuestions.length;
    const approvedQuestions = generatedQuestions.filter(
      (q) =>
        q.localValidationStatus === "APPROVED" ||
        (!q.localValidationStatus && q.validationStatus === "APPROVED")
    ).length;
    const rejectedQuestions = generatedQuestions.filter(
      (q) =>
        q.localValidationStatus === "REJECTED" ||
        (!q.localValidationStatus && q.validationStatus === "REJECTED")
    ).length;
    const pendingQuestions =
      totalQuestions - approvedQuestions - rejectedQuestions;

    return (
      <div className="question-results">
        <div className="alert alert-warning mb-3">
          <h6 className="mb-2">
            <i className="ti ti-alert-circle me-2"></i>Important Note
          </h6>
          <p className="mb-0">
            You must review all questions (either approve or reject) before you
            can click <strong>Done</strong> to save your selections. The Done
            button will remain disabled until all questions have been reviewed.
            If you close this window without clicking Done, all unreviewed
            questions will be automatically approved.
          </p>
        </div>

        <div
          className={`alert ${
            pendingQuestions > 0 ? "alert-warning" : "alert-success"
          } mb-3`}
        >
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              <i
                className={`ti ${
                  pendingQuestions > 0 ? "ti-alert-triangle" : "ti-check-circle"
                } me-2`}
              ></i>
              Review Status
            </h6>
            <div>
              <span className="badge bg-success me-2">
                Approved: {approvedQuestions}
              </span>
              <span className="badge bg-danger me-2">
                Rejected: {rejectedQuestions}
              </span>
              {pendingQuestions > 0 && (
                <span className="badge bg-warning">
                  Pending: {pendingQuestions}
                </span>
              )}
            </div>
          </div>
          {pendingQuestions > 0 ? (
            <p className="mb-0 mt-2">
              Please review all questions before proceeding. {pendingQuestions}{" "}
              questions still need review. The Done button will remain disabled
              until all questions have been reviewed.
            </p>
          ) : (
            <p className="mb-0 mt-2">
              All questions have been reviewed. You can now click Done to save
              your selections.
            </p>
          )}
        </div>

        <h5 className="mb-3">
          Generated Questions ({generatedQuestions.length})
        </h5>
        {generatedQuestions.map((question, index) => {
          // Determine the effective validation status, prioritizing local changes
          const effectiveStatus =
            question.localValidationStatus || question.validationStatus;

          return (
            <div
              key={question.questionId}
              className={`card mb-3 ${
                effectiveStatus === "APPROVED"
                  ? "border-success"
                  : effectiveStatus === "REJECTED"
                  ? "border-danger"
                  : ""
              }`}
            >
              <div className="card-header d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Question {index + 1}</h6>
                <div>
                  <span
                    className={`badge ${
                      question.difficulty === "EASY"
                        ? "bg-success"
                        : question.difficulty === "MEDIUM"
                        ? "bg-warning"
                        : "bg-danger"
                    } me-2`}
                  >
                    {question.difficulty || formData?.difficulty}
                  </span>
                  <button
                    className={`btn btn-sm ${
                      effectiveStatus === "APPROVED"
                        ? "btn-success"
                        : "btn-outline-success"
                    } me-2`}
                    onClick={() => handleAcceptQuestion(question.questionId)}
                    disabled={isBatchProcessing}
                  >
                    <i className="ti ti-check"></i> Accept
                  </button>
                  <button
                    className={`btn btn-sm ${
                      effectiveStatus === "REJECTED"
                        ? "btn-danger"
                        : "btn-outline-danger"
                    }`}
                    onClick={() => handleRejectQuestion(question.questionId)}
                    disabled={isBatchProcessing}
                  >
                    <i className="ti ti-x"></i> Reject
                  </button>
                </div>
              </div>
              <div className="card-body">
                <p className="question-text">{question.questionText}</p>
                <div className="options-list mt-3">
                  {question.options.map((option, optIndex) => (
                    <div
                      key={option.optionId}
                      className={`option-item d-flex align-items-center mb-2 p-2 ${
                        option.isCorrect ? "bg-light-success" : ""
                      }`}
                    >
                      <span className="option-label me-2">
                        {String.fromCharCode(65 + optIndex)}.
                      </span>
                      <span className="option-text">{option.optionText}</span>
                      {option.isCorrect && (
                        <span className="ms-auto badge bg-success">
                          Correct
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                {question.explanation && (
                  <div className="explanation mt-3 p-2 bg-light">
                    <h6 className="mb-1">Explanation:</h6>
                    <p className="mb-0">{question.explanation}</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderConfirmExitDialog = () => {
    return (
      <CustomModal 
        dialogClassName={"modal-md"}
        title="Confirm Exit"
        size="sm"
        show={showConfirmExitDialog} 
        onClose={() => setShowConfirmExitDialog(false)}
        bodyContent={
          <div className="text-center">
            <p className="mb-3 text-dark">
              You have unreviewed questions. If you exit now, all unreviewed
              questions will be automatically APPROVED. Do you want to continue?
            </p>
          </div>
        }
        footerContent={
          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-secondary me-2"
              onClick={() => setShowConfirmExitDialog(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => {
                setShowConfirmExitDialog(false);
                submitQuestionValidations();
              }}
            >
              Exit With Approval
            </button>
          </div>
        }
      />
    )
  }

  // Render batch processing progress indicator
  const renderBatchProgress = () => {
    if (!isBatchProcessing || !batchStatus) return null;

    const {
      totalGenerations = 0,
      completedGenerations = 0,
      failedGenerations = 0,
      totalQuestionsGenerated = 0,
      progressPercentage = 0,
      isComplete = false,
    } = batchStatus;

    // Calculate actual percentage to display
    const displayPercentage = Math.min(100, Math.max(0, progressPercentage));
    const statusText = isComplete ? "Complete" : "In Progress";
    const statusClass = isComplete ? "bg-success" : "bg-primary";

    return (
      <div className="batch-progress-container my-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0">Generation Progress: {statusText}</h6>
          <span>{totalQuestionsGenerated} questions generated</span>
        </div>
        <div className="progress mb-2" style={{ height: "20px" }}>
          <div
            className={`progress-bar ${statusClass}`}
            role="progressbar"
            style={{ width: `${displayPercentage}%` }}
            aria-valuenow={displayPercentage}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            {displayPercentage.toFixed(0)}%
          </div>
        </div>
        <div className="batch-stats d-flex justify-content-between text-muted">
          <span>Total Batches: {totalGenerations}</span>
          <span>Completed: {completedGenerations}</span>
          {failedGenerations > 0 && (
            <span className="text-danger">Failed: {failedGenerations}</span>
          )}
        </div>
      </div>
    );
  };

  const submitQuestionValidations = () => {
    if (!assessment?.id || generatedQuestions.length === 0) return;

    const validations = generatedQuestions.map((question) => ({
      questionId: question.questionId,
      status:
        question.localValidationStatus ||
        question.validationStatus ||
        "APPROVED", // Default to APPROVED if not set
      feedback: "", // Optional feedback, not used in current implementation
    }));

    const batchValidationRequest = {
      assessmentId: assessment.id,
      validations: validations,
    };

    setMsgBox({ msg: "Saving your validations...", error: false });

    callFetch(
      `${API.AI_ASSESSMENT}/questions/validate`,
      "PUT",
      getAPIHeaders(),
      null,
      batchValidationRequest
    )
      .then((res) => {
        if (res && typeof res === "object") {
          const { totalProcessed, approvedCount, rejectedCount, errorCount } =
            res;

          const successMsg = `Questions validation completed: ${approvedCount} approved, ${rejectedCount} rejected${
            errorCount > 0 ? `, ${errorCount} errors` : ""
          }`;

          setMsgBox({
            msg: successMsg,
            error: errorCount > 0,
          });

          // Delay the close to show success message
          setTimeout(() => {
            reset();
            const closeButton = document.querySelector(
              ".modal-dialog .btn-close"
            ) as HTMLElement;
            if (closeButton) {
              closeButton.click();
            }
          }, 1500);
        } else {
          setMsgBox({
            msg: "Failed to save question validations: Invalid response format",
            error: true,
          });
        }
      })
      .catch((err) => {
        setMsgBox({
          msg: `Failed to save validations: ${err.message || "Unknown error"}`,
          error: true,
        });
      });
  };

  return (
    <CustomModal
      show={show}
      title={showResults
              ? "Review Generated Questions"
              : "Generate Assessment Questions with AI #" + (formData?.assessmentId)}
      onClose={exit}
      size="lg"
      bodyContent={<>
          {renderConfirmExitDialog()}
          {isLoading && <PreLoader showLoader={true} />}
          {msgBox.msg && (
            <div
              className={`alert ${
                msgBox.error ? "alert-danger" : "alert-success"
              }`}
              role="alert"
            >
              {msgBox.msg}
            </div>
          )}
          {isBatchProcessing && renderBatchProgress()}
          {!showResults ? renderForm() : renderResults()}
          {isBatchProcessing && renderBatchProgress()}
      </>}

      footerContent={          
          <div className="d-flex justify-content-between w-100">
            <div>
              {msgBox.msg && (
                <span className={msgBox.error ? "text-danger" : "text-success"}>
                  {msgBox.msg}
                </span>
              )}
            </div>
            <div>
              <button
                type="button"
                className="btn btn-light me-2"
                onClick={exit}
              >
                { generatedQuestions?.length ? "Save without review" : "Cancel" }
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={
                  showResults
                    ? submitQuestionValidations
                    : handleGenerateQuestions
                }
                disabled={
                  isLoading ||
                  isBatchProcessing ||
                  (showResults && !areAllQuestionsReviewed())
                }
              >
                {isLoading || isBatchProcessing ? (
                  <span>
                    <i className="spinner-border spinner-border-sm me-1"></i>
                    {showResults
                      ? "Processing..."
                      : isBatchProcessing
                      ? "Generating Questions..."
                      : "Please wait..."}
                  </span>
                ) : showResults ? (
                  areAllQuestionsReviewed() ? (
                    "Done"
                  ) : (
                    "Review All Questions First"
                  )
                ) : (
                  "Generate Questions"
                )}
              </button>
            </div>
          </div>
      }
    />
  );
};

export default AiAssessmentGeneration;
